import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Switch,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { getAdminQuestions, updateAdminQuestions } from "./api/underwritingApi";

const TIERS = [
  { value: "terminal", label: "Terminal" },
  { value: "distress", label: "Distress" },
  { value: "crime", label: "Compliance" },
  { value: "graded", label: "Model input" },
];

// Human-readable summary of how a scoring question moves the ARC score.
const effectFor = (q) => {
  switch (q.tier) {
    case "terminal":
      return { label: "Forces ARC 10", color: "#f25a48", bg: "#fdecea" };
    case "distress":
      return { label: `+${q.boost ?? 1} · cap ${q.floor ?? 8}`, color: "#e7a13c", bg: "#fdf3e2" };
    case "crime":
      return { label: "Compliance review", color: "#5186ec", bg: "#eaf0fd" };
    case "graded":
      return { label: "Model input only", color: "#1f9d55", bg: "#e8f6ee" };
    default:
      return { label: "—", color: "#7a8398", bg: "#f0f2f7" };
  }
};

const labelSx = { fontSize: 10.5, fontWeight: 700, color: "#7a8398", letterSpacing: 0.4, textTransform: "uppercase" };

const AdminQuestionsModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ critical: [], nonCritical: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminQuestions();
      setConfig({
        critical: Array.isArray(data?.critical) ? data.critical : [],
        nonCritical: Array.isArray(data?.nonCritical) ? data.nonCritical : [],
      });
    } catch (e) {
      toast.error("Failed to load questions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const setCritical = (id, patch) =>
    setConfig((c) => ({ ...c, critical: c.critical.map((q) => (q.id === id ? { ...q, ...patch } : q)) }));
  const setNonCritical = (id, patch) =>
    setConfig((c) => ({ ...c, nonCritical: c.nonCritical.map((q) => (q.id === id ? { ...q, ...patch } : q)) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await updateAdminQuestions(config);
      if (saved) setConfig({ critical: saved.critical || [], nonCritical: saved.nonCritical || [] });
      toast.success("Questions saved. Applies on each company's next underwrite.");
      onClose?.();
    } catch (e) {
      toast.error("Failed to save questions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1f2a44" }}>Admin Questions</Typography>
            <Typography sx={{ fontSize: 13, color: "#5f6b85", mt: 0.25 }}>
              Edit the questions the underwriting AI asks, toggle them on or off, and tune how much each scoring
              question moves the ARC score. Changes apply on each company's next underwrite.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: "#9aa3b8" }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#fbfcfe" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, gap: 1.5 }}>
            <CircularProgress size={26} sx={{ color: "#5186ec" }} />
            <Typography sx={{ color: "#5f6b85" }}>Loading questions…</Typography>
          </Box>
        ) : (
          <>
            {/* Scoring questions */}
            <Typography sx={{ ...labelSx, mb: 1 }}>Scoring questions · impact the ARC score</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 1 }}>
              {/* header row */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 130px 90px 80px 140px 64px", gap: 1, px: 1 }}>
                {["Question", "Tier", "Weight", "Cap", "Effect on ARC", "On"].map((h) => (
                  <Typography key={h} sx={labelSx}>{h}</Typography>
                ))}
              </Box>
              {config.critical.map((q) => {
                const eff = effectFor(q);
                const weightDisabled = q.tier !== "distress";
                return (
                  <Box
                    key={q.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 130px 90px 80px 140px 64px",
                      gap: 1,
                      alignItems: "center",
                      background: "#fff",
                      border: "1px solid #eef0f5",
                      borderRadius: "10px",
                      p: 1,
                      opacity: q.enabled === false ? 0.55 : 1,
                    }}
                  >
                    <TextField
                      value={q.question}
                      onChange={(e) => setCritical(q.id, { question: e.target.value })}
                      size="small"
                      multiline
                      maxRows={4}
                      fullWidth
                      variant="outlined"
                      InputProps={{ sx: { fontSize: 12.5 } }}
                    />
                    <Select
                      value={q.tier}
                      onChange={(e) => setCritical(q.id, { tier: e.target.value })}
                      size="small"
                      sx={{ fontSize: 12.5 }}
                    >
                      {TIERS.map((t) => (
                        <MenuItem key={t.value} value={t.value} sx={{ fontSize: 12.5 }}>{t.label}</MenuItem>
                      ))}
                    </Select>
                    <Tooltip title={weightDisabled ? "Weight applies to Distress questions only" : "Points added to the model ARC score (capped at Cap)"} arrow>
                      <TextField
                        value={q.boost ?? 1}
                        onChange={(e) => setCritical(q.id, { boost: Number(e.target.value) })}
                        size="small"
                        type="number"
                        disabled={weightDisabled}
                        inputProps={{ step: 0.5, min: 0, max: 10, style: { fontSize: 12.5 } }}
                      />
                    </Tooltip>
                    <TextField
                      value={q.floor ?? 8}
                      onChange={(e) => setCritical(q.id, { floor: Number(e.target.value) })}
                      size="small"
                      type="number"
                      disabled={q.tier === "crime" || q.tier === "graded"}
                      inputProps={{ step: 0.5, min: 0, max: 10, style: { fontSize: 12.5 } }}
                    />
                    <Chip
                      label={eff.label}
                      size="small"
                      sx={{ fontSize: 11, fontWeight: 600, color: eff.color, background: eff.bg, borderRadius: "8px" }}
                    />
                    <Switch
                      checked={q.enabled !== false}
                      onChange={(e) => setCritical(q.id, { enabled: e.target.checked })}
                      size="small"
                    />
                  </Box>
                );
              })}
            </Box>

            <Typography sx={{ fontSize: 11.5, color: "#9aa3b8", mb: 3 }}>
              Terminal → forces ARC 10 (do-not-lend). Distress → raises the model ARC by <b>Weight</b>, capped at{" "}
              <b>Cap</b>. Compliance → routed to review, no score change. Model input → feeds the model only.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Context questions */}
            <Typography sx={{ ...labelSx, mb: 1 }}>Context questions · feed the summary, no direct ARC weight</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {config.nonCritical.map((q) => (
                <Box
                  key={q.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 64px",
                    gap: 1,
                    alignItems: "center",
                    background: "#fff",
                    border: "1px solid #eef0f5",
                    borderRadius: "10px",
                    p: 1,
                    opacity: q.enabled === false ? 0.55 : 1,
                  }}
                >
                  <TextField
                    value={q.question}
                    onChange={(e) => setNonCritical(q.id, { question: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    InputProps={{ sx: { fontSize: 12.5 } }}
                  />
                  <Switch
                    checked={q.enabled !== false}
                    onChange={(e) => setNonCritical(q.id, { enabled: e.target.checked })}
                    size="small"
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#5f6b85" }} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          sx={{ textTransform: "none", background: "#2f57c4", "&:hover": { background: "#264aa8" }, borderRadius: "8px" }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminQuestionsModal;
